<script lang="ts" setup>
import { onBeforeMount, PropType, ref, watch } from "vue";
import { ParametersTemplateModel } from "../models";
import { TaskParametersTable, TaskParametersInput } from "../components";
import { TaskParameterModel, TaskParameterTypeEnum } from "@/components";
import {
  TirPmButton,
  TirPmButtonSizeEnum,
  TirPmButtonVariantEnum,
  TirPmInput,
} from "tir-components";

/**Параметры */
const props = defineProps({
  /**Тип настройки */
  type: { type: Number as PropType<TaskParameterTypeEnum> },
  /**Редактируемый параметр */
  template: { type: Object as PropType<ParametersTemplateModel> },
  /**Заголовок параметра */
  title: { type: String, default: "" },
  /**Список шаблонов */
  templates: { type: Array as PropType<ParametersTemplateModel[]> },
  /**Имя области */
  namespaceName: { type: String },
});
/**События */
const emit = defineEmits<{
  /**Сохранить отредактированный шаблон */
  save: [template: ParametersTemplateModel];
  /**Отмена редактирования */
  cancel: [];
}>();

/**Локальный редактируемый шаблон */
const innerTemplate = ref<ParametersTemplateModel>();
/**Объект для демонстрации добавления тега */
const upsertParameter = ref(new TaskParameterModel());
/**Список шаблонов */
const parameters = ref<TaskParameterModel[]>([]);

/**Отслеживание изменений шаблона из параметров */
watch(() => props.template, setTemplate, { deep: true });

/**Перед рендером */
onBeforeMount(() => setTemplate());

/**Установка локального шаблона */
function setTemplate() {
  innerTemplate.value = new ParametersTemplateModel(props.template);
  parameters.value = innerTemplate.value.ParsedData.map(
    (x) => new TaskParameterModel({ ...x, InUse: false }),
  );
}
/**Редактирование элемента */
const onUpsertParameter = (_parameter: TaskParameterModel) => {
  if (_parameter.Id) {
    const parameter = parameters.value.find((x) => x.Id == _parameter.Id);
    parameter.Name = _parameter.Name;
    parameter.Color = _parameter.Color;
  } else {
    parameters.value.unshift(_parameter);
  }
  upsertParameter.value = new TaskParameterModel({ Color: upsertParameter.value.Color });
};
/**Сохранить шаблон */
const saveTemplate = () => {
  innerTemplate.value.Data = JSON.stringify(parameters.value);
  setTimeout(() => emit("save", innerTemplate.value));
};
/**Отмена редактирования */
const cancelEditing = () => {
  setTimeout(() => emit("cancel"));
};
</script>
<template>
  <div class="template-edit-form">
    <div class="template-edit-form_title">
      <div class="template-edit-form_title_text">Редактирование шаблона</div>
      <div class="template-edit-form_title_controls">
        <TirPmButton
          :size="TirPmButtonSizeEnum.Small"
          :variant="TirPmButtonVariantEnum.PrimaryState"
          @click="saveTemplate"
        >
          Сохранить
        </TirPmButton>
        <TirPmButton
          :size="TirPmButtonSizeEnum.Small"
          :variant="TirPmButtonVariantEnum.SecondaryState"
          @click="cancelEditing"
        >
          Отмена
        </TirPmButton>
      </div>
    </div>
    <TirPmInput v-model="innerTemplate.Name" :is-with-hint="false" label="Название шаблона" />
    <TaskParametersInput
      v-model:upsertParameter="upsertParameter"
      hide-templates
      :type="type"
      :templates="templates"
      :ignoreParameters="parameters"
      :title="title"
      @upsert="onUpsertParameter"
    />
    <TaskParametersTable :parameters="parameters" :type="type" :namespaceName="namespaceName" />
  </div>
</template>
<style lang="scss" scoped>
.template-edit-form {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  &_title {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1rem;
    &_text {
      color: var(--text-primary, #272d37);
      font-weight: 500;
      font-size: 1rem;
    }
    &_controls {
      display: flex;
      gap: 0.25rem;
    }
  }
}
</style>
